import { EMPTY_FILTERS, topicoPassaFiltros, type BcfFilters, type CamposFiltraveis } from '@modules/bcf/filters'
import { facetaDaEtiqueta, type FacetaDaEtiqueta } from '@modules/bcf/lib/facetasDasEtiquetas'

// Que pins BCF aparecem sobre o modelo.
//
// Existe porque um marcador por cada tópico com viewpoint enche
// depressa: uma detecção de conflitos triada em BCF põe um pin por
// conflito, e sem filtro o modelo fica coberto de marcadores sem
// haver por onde os desligar.
//
// Semântica (igual à da lista do módulo BCF, `topicoPassaFiltros`):
// filtros por facetas. Dentro de cada faceta basta um dos valores
// marcados (OU); entre facetas, todas (E); uma faceta sem nada marcado
// não filtra. Marcado quer sempre dizer "só esses", também no estado e
// na origem (até à beta.176 eram caixas "marcado = visível", ao contrário
// dos outros critérios, e as etiquetas eram todas em E: quatro
// prioridades e duas especialidades davam "0 de 1191 pins").
// "Esconder os fechados e resolvidos" fica à parte, pelas extensões do
// ficheiro.

/** O mínimo que o filtro precisa de saber sobre um tópico. */
export interface PinTopic {
  /** Id do viewpoint, que é a identidade do pin. */
  id: string
  title: string
  /** Estado do tópico, tal como está no BCF. Null quando o ficheiro
   *  não o declara. */
  status: string | null
  /** O estado é terminal (fechado, resolvido) segundo as extensões do
   *  BCF de origem. */
  closed: boolean
  /** Nome do BCF a que pertence, para se poder filtrar por ficheiro
   *  quando há vários carregados. */
  source: string
  /** GUID do tópico e id local do BCF, para seguir a lista do módulo.
   *  Opcionais: sem eles o pin não se compara com a lista. */
  topicGuid?: string
  bcfLocalId?: string
  /** Campos do tópico para os filtros do painel (prioridade, responsável,
   *  etiquetas, pesquisa). Opcionais: um pin sem eles conta como "sem". */
  priority?: string | null
  assignedTo?: string | null
  labels?: string[]
  description?: string | null
  /** O grupo do tópico (etiqueta `Grupo <id>` ou RelatedTopics), com a
   *  chave e o nome dos blocos do módulo BCF (bcfAgrupar.ts). */
  grupo?: { chave: string; nome: string } | null
}

/** Os critérios do painel que escolhem (vazio: todos). Marcado = só
 *  esses, em todos. A pesquisa, a prioridade, o responsável e as
 *  etiquetas são os do módulo BCF (`BcfFilters`) e aplicam-se com a mesma
 *  função da lista do módulo (`topicoPassaFiltros`); o grupo, o estado e
 *  a origem são dos pins. */
export interface CriteriosDosPins {
  /** Texto a procurar no título e na descrição. */
  pesquisa: string
  /** Prioridades escolhidas; '' é "Sem prioridade". */
  prioridades: string[]
  /** Responsáveis escolhidos; '' é "Sem responsável". */
  responsaveis: string[]
  /** Etiquetas escolhidas. Dividem-se em facetas pelo tipo
   *  (especialidade, nível, outras; `facetasDasEtiquetas.ts`): OU dentro
   *  de cada uma, E entre elas. */
  etiquetas: string[]
  /** Chaves dos grupos escolhidos (um deles basta). */
  grupos: string[]
  /** Estados escolhidos, pelo texto do estado (`statusKey`; um tópico
   *  sem estado conta como `SEM_ESTADO`). */
  estados: string[]
  /** Ficheiros BCF escolhidos, pelo nome. */
  origens: string[]
}

export const CRITERIOS_VAZIOS: CriteriosDosPins = {
  pesquisa: '',
  prioridades: [],
  responsaveis: [],
  etiquetas: [],
  grupos: [],
  estados: [],
  origens: [],
}

/** As facetas do painel, pela ordem em que aparecem. As três de
 *  etiquetas partilham `CriteriosDosPins.etiquetas`. */
export type FacetaDosPins =
  | 'prioridade'
  | 'estado'
  | 'grupo'
  | 'especialidade'
  | 'nivel'
  | 'outras'
  | 'responsavel'
  | 'origem'

export const FACETAS_DOS_PINS: readonly FacetaDosPins[] = [
  'prioridade',
  'estado',
  'grupo',
  'especialidade',
  'nivel',
  'outras',
  'responsavel',
  'origem',
]

export const ROTULO_FACETA_DOS_PINS: Record<FacetaDosPins, string> = {
  prioridade: 'Prioridade',
  estado: 'Estado',
  grupo: 'Grupo',
  especialidade: 'Especialidade',
  nivel: 'Nível',
  outras: 'Outras etiquetas',
  responsavel: 'Responsável',
  origem: 'Origem (BCF)',
}

const FACETA_DE_ETIQUETA: Partial<Record<FacetaDosPins, FacetaDaEtiqueta>> = {
  especialidade: 'especialidade',
  nivel: 'nivel',
  outras: 'outras',
}

/** O campo dos critérios onde vive cada faceta. */
function campoDaFaceta(f: FacetaDosPins): Exclude<keyof CriteriosDosPins, 'pesquisa'> {
  switch (f) {
    case 'prioridade':
      return 'prioridades'
    case 'estado':
      return 'estados'
    case 'grupo':
      return 'grupos'
    case 'responsavel':
      return 'responsaveis'
    case 'origem':
      return 'origens'
    default:
      return 'etiquetas'
  }
}

/** Os valores marcados numa faceta. */
export function escolhidosDaFaceta(c: CriteriosDosPins, f: FacetaDosPins): string[] {
  const valores = c[campoDaFaceta(f)]
  const fe = FACETA_DE_ETIQUETA[f]
  return fe ? valores.filter((v) => facetaDaEtiqueta(v) === fe) : valores
}

/** Os critérios sem nada marcado numa faceta (as outras ficam). */
export function semFaceta(c: CriteriosDosPins, f: FacetaDosPins): CriteriosDosPins {
  const campo = campoDaFaceta(f)
  const fe = FACETA_DE_ETIQUETA[f]
  return { ...c, [campo]: fe ? c.etiquetas.filter((v) => facetaDaEtiqueta(v) !== fe) : [] }
}

/** Marca ou desmarca um valor numa faceta. */
export function alternarNaFaceta(c: CriteriosDosPins, f: FacetaDosPins, valor: string): Partial<CriteriosDosPins> {
  const campo = campoDaFaceta(f)
  return { [campo]: toggleHidden(c[campo], valor) }
}

/** Quantos filtros estão activos, por faceta e não por valor: a
 *  pesquisa conta um, cada faceta com alguma coisa marcada conta um (quatro
 *  prioridades marcadas são um filtro), e "Esconder os fechados" conta um.
 *  "Seguir os filtros do módulo BCF" não conta: é uma ligação, e o
 *  "Limpar filtros" não lhe mexe. */
export function filtrosActivos(c: CriteriosDosPins, hideClosed = false): number {
  let n = c.pesquisa.trim() ? 1 : 0
  for (const f of FACETAS_DOS_PINS) if (escolhidosDaFaceta(c, f).length > 0) n++
  return n + (hideClosed ? 1 : 0)
}

/** O que está marcado, faceta a faceta, para mostrar por extenso
 *  ("Prioridade: Critical, High"). */
export function resumoDosFiltros(c: CriteriosDosPins, hideClosed = false, nomeDoGrupo?: (chave: string) => string): string[] {
  const linhas: string[] = []
  if (c.pesquisa.trim()) linhas.push(`Pesquisa: "${c.pesquisa.trim()}"`)
  for (const f of FACETAS_DOS_PINS) {
    const v = escolhidosDaFaceta(c, f)
    if (v.length === 0) continue
    const nomes = v.map((x) => (f === 'grupo' && nomeDoGrupo ? nomeDoGrupo(x) : rotuloDoValor(f, x)))
    linhas.push(`${ROTULO_FACETA_DOS_PINS[f]}: ${nomes.join(', ')}`)
  }
  if (hideClosed) linhas.push('Sem os fechados e resolvidos')
  return linhas
}

/** Como se escreve um valor vazio em cada faceta. */
const PRIORIDADE_PT: Record<string, string> = {
  critical: 'Crítica',
  high: 'Alta',
  normal: 'Média',
  medium: 'Média',
  low: 'Baixa',
}

export function rotuloDoValor(f: FacetaDosPins, valor: string): string {
  // O BCF guarda a prioridade em inglês (Critical, High...): mostra-se em
  // português; outros valores (de outras plataformas) ficam como vêm.
  if (f === 'prioridade' && PRIORIDADE_PT[valor.toLowerCase()]) return PRIORIDADE_PT[valor.toLowerCase()]
  if (valor !== '') return valor
  if (f === 'prioridade') return 'Sem prioridade'
  if (f === 'responsavel') return 'Sem responsável'
  return '(sem)'
}

/** A lista do módulo BCF que os pins seguem: o BCF aberto na lista e os
 *  tópicos que ela mostra (filtros e blocos abertos). */
export interface ListaDoModulo {
  bcfLocalId: string
  guids: ReadonlySet<string>
}

export interface PinFilter {
  /** Esconder tudo o que está fechado ou resolvido. É o filtro que
   *  mais vezes se quer, por isso tem interruptor próprio em vez de
   *  obrigar a escolher os estados um a um. */
  hideClosed: boolean
  /** Estados escolhidos (null ou vazio: todos). */
  estados?: ReadonlySet<string> | null
  /** Ficheiros BCF escolhidos (null ou vazio: todos). */
  origens?: ReadonlySet<string> | null
  /** Seguir a lista do módulo BCF: só os tópicos que ela mostra. Null (ou
   *  ausente) quando a opção está desligada ou a lista não está aberta:
   *  aí não há nada a seguir e ficam só os filtros do painel. */
  lista?: ListaDoModulo | null
  /** Os critérios do módulo BCF já montados (null sem nenhum). */
  bcf?: BcfFilters | null
  /** Grupos escolhidos (null ou vazio: todos). */
  grupos?: ReadonlySet<string> | null
}

/** Chave de um tópico sem estado declarado. */
export const SEM_ESTADO = '(sem estado)'

export const PIN_FILTER_VAZIO: PinFilter = {
  hideClosed: false,
}

export function statusKey(topic: PinTopic): string {
  return topic.status?.trim() || SEM_ESTADO
}

/** O pin visto como tópico pelos filtros do módulo BCF. */
export function comoTopico(t: PinTopic): CamposFiltraveis {
  return {
    title: t.title,
    description: t.description ?? null,
    status: t.status,
    priority: t.priority ?? null,
    type: null,
    assignedTo: t.assignedTo ?? null,
    labels: t.labels ?? [],
    dueDate: null,
  }
}

/** O tópico passa o filtro e deve mostrar pin. */
export function pinVisible(topic: PinTopic, filter: PinFilter): boolean {
  if (filter.hideClosed && topic.closed) return false
  if (filter.estados && filter.estados.size > 0 && !filter.estados.has(statusKey(topic))) return false
  if (filter.origens && filter.origens.size > 0 && !filter.origens.has(topic.source)) return false
  if (filter.grupos && filter.grupos.size > 0) {
    if (!topic.grupo || !filter.grupos.has(topic.grupo.chave)) return false
  }
  if (filter.bcf && !topicoPassaFiltros(comoTopico(topic), filter.bcf)) return false
  if (filter.lista) {
    // Um pin de outro BCF, ou de um tópico que a lista não mostra, sai.
    if (topic.bcfLocalId !== filter.lista.bcfLocalId) return false
    if (!topic.topicGuid || !filter.lista.guids.has(topic.topicGuid)) return false
  }
  return true
}

/** O que monta o filtro: o painel e a lista do módulo. */
export interface EntradaDoFiltro {
  hideClosed: boolean
  seguirModulo: boolean
  vista: { bcfLocalId: string; guids: readonly string[] } | null
  criterios?: CriteriosDosPins
}

/** O filtro completo: o do painel e, com "Seguir os filtros do módulo
 *  BCF" ligado e a lista aberta, a lista do módulo. */
export function filtroDosPins(o: EntradaDoFiltro): PinFilter {
  const c = o.criterios ?? CRITERIOS_VAZIOS
  const comBcf = c.pesquisa.trim() !== '' || c.prioridades.length > 0 || c.responsaveis.length > 0 || c.etiquetas.length > 0
  return {
    hideClosed: o.hideClosed,
    estados: c.estados.length > 0 ? new Set(c.estados) : null,
    origens: c.origens.length > 0 ? new Set(c.origens) : null,
    lista:
      o.seguirModulo && o.vista
        ? { bcfLocalId: o.vista.bcfLocalId, guids: new Set(o.vista.guids) }
        : null,
    // Os fechados são do `hideClosed` (pelas extensões do ficheiro), não
    // do `showClosed` do módulo (pelo texto do estado).
    bcf: comBcf
      ? {
          ...EMPTY_FILTERS,
          search: c.pesquisa,
          priorities: new Set(c.prioridades),
          assignees: new Set(c.responsaveis),
          labels: new Set(c.etiquetas),
        }
      : null,
    grupos: c.grupos.length > 0 ? new Set(c.grupos) : null,
  }
}

/** Os valores de um pin numa faceta ('' quando não tem). Nas etiquetas, um
 *  pin pode ter vários (ou nenhum); as `Grupo <id>` são da faceta Grupo,
 *  pela chave do bloco. */
export function valoresDaFaceta(t: PinTopic, f: FacetaDosPins): string[] {
  switch (f) {
    case 'prioridade':
      return [(t.priority ?? '').trim() ? (t.priority as string) : '']
    case 'responsavel':
      return [(t.assignedTo ?? '').trim() ? (t.assignedTo as string) : '']
    case 'estado':
      return [statusKey(t)]
    case 'origem':
      return [t.source]
    case 'grupo':
      return t.grupo ? [t.grupo.chave] : []
    default: {
      const fe = FACETA_DE_ETIQUETA[f]
      return [...new Set((t.labels ?? []).filter((l) => facetaDaEtiqueta(l) === fe))]
    }
  }
}

/** Contagens facetadas: para cada faceta, quantos pins teria cada valor
 *  com os OUTROS filtros todos aplicados (a pesquisa, os fechados, a lista
 *  do módulo e as restantes facetas), mas não o da própria faceta. É o que
 *  deixa ver, antes de clicar, que combinação dá zero: com "Especialidade:
 *  MAC" marcado, o "P2" do Nível diz quantos de MAC há no P2.
 *
 *  Cada faceta corre o filtro inteiro (`pinVisible`) sobre todos os pins:
 *  com 8 facetas e 1191 pins são ~10 000 verificações, sem custo visível. */
export function contagensFacetadas(
  topics: readonly PinTopic[],
  o: EntradaDoFiltro,
): Record<FacetaDosPins, Map<string, number>> {
  const c = o.criterios ?? CRITERIOS_VAZIOS
  const out = {} as Record<FacetaDosPins, Map<string, number>>
  for (const f of FACETAS_DOS_PINS) {
    const filtro = filtroDosPins({ ...o, criterios: semFaceta(c, f) })
    const mapa = new Map<string, number>()
    for (const t of topics) {
      if (!pinVisible(t, filtro)) continue
      for (const v of valoresDaFaceta(t, f)) mapa.set(v, (mapa.get(v) ?? 0) + 1)
    }
    out[f] = mapa
  }
  return out
}

/** Um filtro activo e quantos pins passariam sem ele (os outros ficam). */
export interface SemEsteFiltro {
  rotulo: string
  passariam: number
}

/** Quando nada passa: para cada filtro activo, quantos pins passariam se
 *  só esse se tirasse. Diz ao utilizador que filtro deixa a combinação a
 *  zero, sem ter de os experimentar um a um. Vazio quando passa algum, ou
 *  quando nenhum filtro sozinho resolve (aí são dois ou mais juntos). */
export function semCadaFiltro(topics: readonly PinTopic[], o: EntradaDoFiltro): SemEsteFiltro[] {
  const c = o.criterios ?? CRITERIOS_VAZIOS
  const contar = (e: EntradaDoFiltro) => {
    const f = filtroDosPins(e)
    let n = 0
    for (const t of topics) if (pinVisible(t, f)) n++
    return n
  }
  if (contar(o) > 0) return []
  const out: SemEsteFiltro[] = []
  if (c.pesquisa.trim()) out.push({ rotulo: 'a pesquisa', passariam: contar({ ...o, criterios: { ...c, pesquisa: '' } }) })
  for (const f of FACETAS_DOS_PINS) {
    if (escolhidosDaFaceta(c, f).length === 0) continue
    out.push({ rotulo: ROTULO_FACETA_DOS_PINS[f], passariam: contar({ ...o, criterios: semFaceta(c, f) }) })
  }
  if (o.hideClosed) out.push({ rotulo: 'Esconder os fechados', passariam: contar({ ...o, hideClosed: false }) })
  if (o.seguirModulo && o.vista) {
    out.push({ rotulo: 'Seguir os filtros do módulo BCF', passariam: contar({ ...o, seguirModulo: false }) })
  }
  return out.filter((s) => s.passariam > 0)
}

/** Contagem por valor de um campo, para as listas do painel: o valor
 *  (como está no tópico; '' quando não tem) e quantos pins o têm. Nas
 *  etiquetas, um pin conta em cada uma; as `Grupo <id>` ficam de fora
 *  (são do critério Grupo). */
export function contarPorCampo(
  topics: readonly PinTopic[],
  campo: 'priority' | 'assignedTo' | 'labels',
): Array<{ valor: string; total: number }> {
  const mapa = new Map<string, number>()
  for (const t of topics) {
    const valores =
      campo === 'labels'
        ? [...new Set((t.labels ?? []).filter((l) => facetaDaEtiqueta(l) !== 'grupo'))]
        : [(t[campo] ?? '').trim() ? (t[campo] as string) : '']
    for (const v of valores) mapa.set(v, (mapa.get(v) ?? 0) + 1)
  }
  return [...mapa.entries()]
    .map(([valor, total]) => ({ valor, total }))
    .sort((a, b) => b.total - a.total || a.valor.localeCompare(b.valor, 'pt'))
}

/** Os grupos dos pins, com o nome e quantos pins têm, por ordem de
 *  tamanho. */
export function contarGrupos(topics: readonly PinTopic[]): Array<{ chave: string; nome: string; total: number }> {
  const mapa = new Map<string, { nome: string; total: number }>()
  for (const t of topics) {
    if (!t.grupo) continue
    const e = mapa.get(t.grupo.chave)
    if (e) e.total += 1
    else mapa.set(t.grupo.chave, { nome: t.grupo.nome, total: 1 })
  }
  return [...mapa.entries()]
    .map(([chave, v]) => ({ chave, ...v }))
    .sort((a, b) => b.total - a.total || a.nome.localeCompare(b.nome, 'pt', { numeric: true }))
}

/** Os ficheiros BCF dos pins, por ordem de tamanho. */
export function contarOrigens(topics: readonly PinTopic[]): Array<{ valor: string; total: number }> {
  const mapa = new Map<string, number>()
  for (const t of topics) mapa.set(t.source, (mapa.get(t.source) ?? 0) + 1)
  return [...mapa.entries()]
    .map(([valor, total]) => ({ valor, total }))
    .sort((a, b) => b.total - a.total || a.valor.localeCompare(b.valor, 'pt'))
}

export function filterPins<T extends PinTopic>(
  topics: T[],
  filter: PinFilter,
): T[] {
  return topics.filter((t) => pinVisible(t, filter))
}

/** Contagem por estado, para o painel poder mostrar quantos há de cada
 *  e não obrigar a adivinhar o que se está a esconder. */
export function countByStatus(
  topics: readonly PinTopic[],
): Array<{ status: string; total: number; closed: boolean }> {
  const mapa = new Map<string, { total: number; closed: boolean }>()
  for (const t of topics) {
    const k = statusKey(t)
    const linha = mapa.get(k) ?? { total: 0, closed: t.closed }
    linha.total += 1
    // Um estado é tratado como terminal se ALGUM tópico com ele o for:
    // as extensões vêm do ficheiro e podem divergir entre BCFs.
    linha.closed = linha.closed || t.closed
    mapa.set(k, linha)
  }
  return [...mapa.entries()]
    .map(([status, v]) => ({ status, ...v }))
    .sort((a, b) => b.total - a.total || a.status.localeCompare(b.status))
}

/** Alterna a presença de um valor numa lista. */
export function toggleHidden(lista: string[], valor: string): string[] {
  return lista.includes(valor)
    ? lista.filter((v) => v !== valor)
    : [...lista, valor]
}
